import { useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../../hooks/useApiHelper";
import useNotification from "../../components/shared/Notifications/useNotifications";
import { IDefaultResponse } from "../../models/DefaultResponse";
import {
  IPinnacleImportSummary,
  IPinnacleItemType,
  IPinnacleMatchSheetRow,
  ISavePinnacleRule,
} from "../../models/PinnacleMapping/PinnacleMapping";
import { PinnacleMappingQueryKeys } from "./keys";

export default function usePinnacleMappingMutations() {
  const { post, put, del } = useApiHelper();
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();

  // A mapping change moves suggestions (an added one disappears) and the parts preview, so all of
  // the page's data is refreshed rather than picking out which parts went stale.
  const refresh = () => queryClient.invalidateQueries({ queryKey: PinnacleMappingQueryKeys.all() });

  const handlers = (fallback: string) => ({
    onSuccess: (res: IDefaultResponse<unknown>) => {
      refresh();
      if (res?.message) addNotification({ variant: "success", primaryText: res.message });
    },
    onError: (error: Error) => {
      addNotification({ variant: "error", primaryText: fallback, secondaryText: error.message });
    },
  });

  type ItemTypeResponse = IDefaultResponse<IPinnacleItemType>;
  type SummaryResponse = IDefaultResponse<IPinnacleImportSummary>;

  const createRule = useMutation({
    mutationFn: ({ itemTypeId, rule }: { itemTypeId: string; rule: ISavePinnacleRule }) =>
      post<ItemTypeResponse>(`/pinnacle-mapping/item-types/${itemTypeId}/rules`, rule),
    ...handlers("Could not add the mapping"),
  });

  const updateRule = useMutation({
    mutationFn: ({ ruleId, rule }: { ruleId: string; rule: ISavePinnacleRule }) =>
      put<ItemTypeResponse>(`/pinnacle-mapping/rules/${ruleId}`, rule),
    ...handlers("Could not save the mapping"),
  });

  const confirmRule = useMutation({
    mutationFn: (ruleId: string) => post<ItemTypeResponse>(`/pinnacle-mapping/rules/${ruleId}/confirm`, {}),
    ...handlers("Could not mark the mapping as checked"),
  });

  const deleteRule = useMutation({
    mutationFn: (ruleId: string) => del<ItemTypeResponse>(`/pinnacle-mapping/rules/${ruleId}`),
    ...handlers("Could not remove the mapping"),
  });

  const acceptSuggestion = useMutation({
    mutationFn: (suggestionId: string) =>
      post<ItemTypeResponse>(`/pinnacle-mapping/suggestions/${suggestionId}/accept`, {}),
    ...handlers("Could not add the suggestion"),
  });

  const dismissSuggestion = useMutation({
    mutationFn: (suggestionId: string) =>
      post<ItemTypeResponse>(`/pinnacle-mapping/suggestions/${suggestionId}/dismiss`, {}),
    ...handlers("Could not dismiss the suggestion"),
  });

  const createItemType = useMutation({
    mutationFn: (name: string) => post<ItemTypeResponse>(`/pinnacle-mapping/item-types`, { name }),
    ...handlers("Could not add the item type"),
  });

  const setIgnored = useMutation({
    mutationFn: ({ itemTypeId, ignored }: { itemTypeId: string; ignored: boolean }) =>
      put<ItemTypeResponse>(`/pinnacle-mapping/item-types/${itemTypeId}`, { ignored }),
    ...handlers("Could not update the item type"),
  });

  const importLegacy = useMutation({
    mutationFn: () => post<SummaryResponse>(`/pinnacle-mapping/import/legacy`, {}),
    ...handlers("Could not import the old Pinnacle names"),
  });

  const importMatchSheet = useMutation({
    mutationFn: (rows: IPinnacleMatchSheetRow[]) =>
      post<SummaryResponse>(`/pinnacle-mapping/import/match-sheet`, { rows }),
    ...handlers("Could not import the match sheet"),
  });

  const buildSuggestions = useMutation({
    mutationFn: () => post<SummaryResponse>(`/pinnacle-mapping/suggestions/build`, {}),
    ...handlers("Could not build suggestions"),
  });

  /** Sends the confirmed mappings to customers' Cores now rather than waiting for the nightly sync. */
  const pushToCustomers = useMutation({
    mutationFn: (customerIds: string[]) => post(`/part/categories/sync`, { customers: customerIds }),
    onSuccess: () => addNotification({ variant: "success", primaryText: "Category sync queued", secondaryText: "Mappings reach each customer over the next few minutes." }),
    onError: (error: Error) => addNotification({ variant: "error", primaryText: "Could not queue the sync", secondaryText: error.message }),
  });

  return {
    createRule,
    updateRule,
    confirmRule,
    deleteRule,
    acceptSuggestion,
    dismissSuggestion,
    createItemType,
    setIgnored,
    importLegacy,
    importMatchSheet,
    buildSuggestions,
    pushToCustomers,
  };
}
